"use client";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Header from "@cloudscape-design/components/header";
import KeyValuePairs from "@cloudscape-design/components/key-value-pairs";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Table from "@cloudscape-design/components/table";

import {
  StatusBadge,
  type FigureStatus,
} from "@/features/dashboard/report/StatusBadge";
import { useInspect } from "@/features/inspect/InspectContext";
import { useLineage, type LineageSource } from "@/features/inspect/useLineage";

const KT = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });

function SourceTable({ source }: { source: LineageSource }): React.JSX.Element {
  const columnDefinitions = source.columns.map((col, i) => ({
    id: col,
    header: col,
    cell: (row: unknown[]) => {
      const v = row[i];
      // Right-align numerics with thousands separators so the column scans cleanly.
      return typeof v === "number" ? (
        <Box textAlign="right">{KT.format(v)}</Box>
      ) : (
        String(v)
      );
    },
  }));
  return (
    <SpaceBetween size="xs">
      <Box variant="small" color="text-status-inactive">
        {source.granularity === "per-borough"
          ? "The per-borough figures that sum to this number."
          : "The component figures that make up this number."}
      </Box>
      <Table
        variant="embedded"
        items={source.rows}
        columnDefinitions={columnDefinitions}
        empty="No source rows."
      />
    </SpaceBetween>
  );
}

/**
 * The Inspect drawer body (feature 009, US2): the three layers behind a figure — Source data /
 * Rule / Computed result — plus run provenance. Renders only; computes nothing. The Source layer
 * appears for RF-internal users (the server returns it only for super-admins); LA-planners see a
 * note instead (tiered disclosure, D7).
 */
export function InspectDrawerContent(): React.JSX.Element {
  const { ref } = useInspect();
  const { data, loading, error } = useLineage(ref);

  if (ref === null) {
    return (
      <Box variant="p" color="text-status-inactive" padding={{ top: "s" }}>
        Select a figure&apos;s &ldquo;Inspect this number&rdquo; to trace where
        it came from — its source data, the rule applied, and the computed
        result.
      </Box>
    );
  }
  if (loading) return <Spinner />;
  if (error) {
    return (
      <Alert type="warning" header="Could not load lineage">
        {error}
      </Alert>
    );
  }
  if (!data) return <></>;

  // No engine figure: either a non-engine figure (rule says so) or no published run.
  if (data.figure === null) {
    if (data.rule) {
      return (
        <Alert type="info" header={data.rule.name}>
          {data.rule.summary}
        </Alert>
      );
    }
    return (
      <Alert type="info" header="Nothing to inspect">
        There is no published run for this output yet.
      </Alert>
    );
  }

  const status = (data.figure.status ?? undefined) as FigureStatus | undefined;
  const prov = data.figure.provenance;

  return (
    <SpaceBetween size="m">
      <Header variant="h3">
        <SpaceBetween size="xxs" direction="horizontal">
          <Box variant="strong">{KT.format(data.figure.value)}</Box>
          {status ? <StatusBadge status={status} /> : null}
        </SpaceBetween>
      </Header>

      {data.computedExplanation ? (
        <Alert type="info" header="Why this is Computed">
          {data.computedExplanation}
        </Alert>
      ) : null}

      <ExpandableSection
        headerText="Source data"
        defaultExpanded
        variant="footer"
      >
        {data.source ? (
          <SourceTable source={data.source} />
        ) : (
          <Box variant="small" color="text-status-inactive">
            The underlying source rows are available to RF-internal users.
          </Box>
        )}
      </ExpandableSection>

      <ExpandableSection
        headerText="Rule applied"
        defaultExpanded
        variant="footer"
      >
        {data.rule ? (
          <SpaceBetween size="xxs">
            <Box variant="strong">{data.rule.name}</Box>
            <Box variant="p">{data.rule.summary}</Box>
            {data.rule.configRef ? (
              <Box variant="small" color="text-status-inactive">
                Reference: {data.rule.configRef}
              </Box>
            ) : null}
          </SpaceBetween>
        ) : (
          <Box variant="small" color="text-status-inactive">
            No rule recorded for this figure.
          </Box>
        )}
      </ExpandableSection>

      <ExpandableSection
        headerText="Computed result"
        defaultExpanded
        variant="footer"
      >
        <SpaceBetween size="xxs">
          <Box variant="p">
            {KT.format(data.figure.value)}
            {status ? ` — ${status}` : ""}
            {status === "Reproduced"
              ? " (matched to the published study to the tonne)."
              : data.computedExplanation
                ? "."
                : " (computed live; no published reference recorded)."}
          </Box>
        </SpaceBetween>
      </ExpandableSection>

      {prov ? (
        <KeyValuePairs
          columns={3}
          items={[
            { label: "Engine version", value: prov.engineVersion },
            { label: "Vintage", value: String(prov.vintage) },
            { label: "Run", value: prov.runId.slice(0, 8) },
          ]}
        />
      ) : null}
    </SpaceBetween>
  );
}
